import {
  Col,
  Divider,
  Row,
  Space,
  Tag,
  Typography,
  Modal,
  Rate,
  Select,
  Button,
  Badge,
  Segmented,
  message,
} from 'antd';
import Link from 'antd/lib/typography/Link';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryParam } from 'use-query-params';
import { FlexBox } from '../../../../components/atoms/FlexBox';
import { CycleToggle } from '../../../../components/atoms/ItemToggle';
import { UserAvatar } from '../../../../components/atoms/UserAvatar';
import ReviewPreview from '../../../../components/molecules/ReviewPreview/ReviewPreview';
import { useConnection, useUserInfo } from '../../../../connection/Application';
import { PerformanceCycle } from '../../../../entity/PerformanceCycle';
import { WebUxPage, WebUxPageContent, WebUxPageItem } from '../../webux-layouts/WebUxPage';
import {
  AlignLeftOutlined,
  ExclamationCircleOutlined,
  ArrowRightOutlined,
  RetweetOutlined,
  CaretDownOutlined,
  CaretUpOutlined,
  StarFilled,
} from '@ant-design/icons';
import { Criteria } from '../review-settings/reviewQuestions';
import ReactHtmlParser from '../../../../utils/SafeHtmlParser';
import { RichText } from '../../../../components/RichText/RichText';
import { PageHeader } from '@ant-design/pro-layout';
import {
  DataTable,
  DataTablePagination,
} from '../../../../components/molecules/DataTable/DataTable';
const { confirm } = Modal;
const { Title, Text } = Typography;

export interface InternalReviewModel {
  criteriaSet?: Criteria[];
  overallComment?: string;
  submitTime?: string;
  cycle?: number;
  submittingUser?: number;
}

const regex = /(<([^>]+)>)/gi;

const getObjectionStatusMeta = (status?: string) => {
  const normalizedStatus = status?.toLowerCase();

  if (!normalizedStatus) {
    return null;
  }

  return {
    color:
      normalizedStatus === 'accepted'
        ? 'green'
        : normalizedStatus === 'rejected'
        ? 'red'
        : 'orange',
    label: normalizedStatus.charAt(0).toUpperCase() + normalizedStatus.slice(1),
  };
};

const InternalCustomerAnswers: FC = () => {
  const [cycle, setCycle] = useQueryParam<number | undefined>('cycle');
  const cycleRef = useRef(cycle);
  const user = useUserInfo();
  const [cycles, setCycles] = useState<PerformanceCycle[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [tableData, setTableData] = useState<any>([]);
  const [teams, setTeams] = useState<any>([]);
  const [filterTeam, setFilterTeam] = useState<string>('');
  const connection = useConnection();
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [rejectModalVisible, setRejectModalVisible] = useState<boolean>(false);
  const [recallModalVisible, setRecallModalVisible] = useState<boolean>(false);
  const [recallModalMode, setRecallModalMode] = useState<'recall' | 'accept-objection'>('recall');
  const [rejectReason, setRejectReason] = useState<string>('');
  const [recallReason, setRecallReason] = useState<string>('');
  const [filterType, setFilterType] = useState<string>('all');
  const [showContent, setShowContent] = useState<boolean>(true);
  const [record, setRecord] = useState<any>();
  const [fetching, setFetching] = useState<boolean>(false);
  const [filter, setFilter] = useState<any>({});
  const [total, setTotal] = useState<number>(0);
  const [pagination, setPagination] = useState<DataTablePagination>({ page: 1, size: 10 });
  const [submitData, setSubmitData] = useState<any>({
    criteriaSet: [],
    overallComment: '',
    submitTime: '',
    cycle: cycle,
    submittingUser: 0,
  });

  useEffect(() => {
    setFilter({
      ...filter,
      status: filterType,
    });
    search(
      {
        ...filter,
        status: filterType,
      },
      { page: 1, size: pagination.size },
      filterTeam,
    );
    // eslint-disable-next-line
  }, [filterType]);

  const search = useCallback(
    (filter: any, pagination: DataTablePagination, filterTeam: string) => {
      setLoading(true);
      if (cycle) {
        connection
          .get(
            `goals/internal-customers/${cycle}/approve`,
            filterTeam === ''
              ? {
                  ...pagination,
                  ...filter,
                }
              : {
                  ...pagination,
                  ...filter,
                  teamId: filterTeam,
                },
          )
          .then((res: any) => {
            const data = res.data.map((item: any) => {
              return { ...item, teamName: item.team.name, key: item.id };
            });
            setTableData(data);
            setTeams(res.teams);
            setTotal(res.total);
          })
          .finally(() => setLoading(false));
      }
    },
    [cycle, connection, setTableData, setTeams, setTotal, setLoading],
  );

  useEffect(() => {
    setLoading(true);
    if (cycle) {
      search(filter, pagination, filterTeam);
    }
    // eslint-disable-next-line
  }, [connection, user?.userId, fetching]);

  useEffect(() => {
    setLoading(true);
    if (cycle) {
      search(filter, { size: pagination.size, page: 1 }, filterTeam);
    }
    // eslint-disable-next-line
  }, [connection, cycle, user?.userId]);

  const columns = useMemo(() => {
    return [
      {
        title: 'Team Name',
        dataIndex: 'teamName',
        key: 'teamName',
        // eslint-disable-next-line react/display-name
        render: (text: string, record: any) => (
          <Space>
            <UserAvatar type={record.itemType} src={record?.reviewer?.image ?? ''} />
            {record.status !== 'not submitted' && record.status !== 'drafted' ? (
              <Link
                onClick={() => {
                  setSubmitData({
                    ...submitData,
                    criteriaSet: record?.criteriaSet?.criterias.map((c: any) => ({
                      ...c,
                      answer: ((c?.answer ?? 0) / 100) * c.type ?? 5,
                    })),
                    overallComment: record?.overallComment ?? '',
                  });
                  setRecord(record);
                  setModalVisible(true);
                }}>
                {`${record?.reviewer?.name ?? ''} `}
                <ArrowRightOutlined />
                {` ${record.team.name}`}
              </Link>
            ) : (
              <>
                {`${record?.reviewer?.name ?? ''} `}
                <ArrowRightOutlined />
                {` ${record.team.name}`}
              </>
            )}
          </Space>
        ),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        // eslint-disable-next-line react/display-name
        render: (text: string, record: any) => (
          <Space direction="vertical" size={0}>
            <Space>
              {(record.status == '' ||
                record.status == 'drafted' ||
                record.status == 'not submitted') && <Tag color={'blue'}>Not submitted</Tag>}
              {record.status == 'submitted' && <Tag color={'yellow'}>Submitted</Tag>}
              {record.status == 'approved' && <Tag color={'green'}>Approved</Tag>}
              {record.status == 'rejected' && <Tag color={'red'}>Rejected</Tag>}
              {record.status == 'recalled' && <Tag color={'orange'}>Recalled</Tag>}
            </Space>
            {record.objectionStatus && (
              <Tag
                color={
                  record.objectionStatus === 'Pending'
                    ? 'warning'
                    : record.objectionStatus === 'Accepted'
                    ? 'success'
                    : 'error'
                }
                style={{ marginTop: 4 }}>
                Objection {record.objectionStatus}
              </Tag>
            )}
          </Space>
        ),
      },
      {
        title: 'Score',
        dataIndex: 'marks',
        key: 'marks',
        // eslint-disable-next-line react/display-name
        render: (text: string, record: any) => (
          <Space>
            {!(
              record.status == '' ||
              record.status == 'drafted' ||
              record.status == 'not submitted'
            ) && (
              <Badge
                count={
                  record.criteriaSet?.criterias.reduce((x: any, y: any) => x + y.answer, 0) /
                    record.criteriaSet?.criterias.length +
                  '%'
                }
                showZero
                overflowCount={120}
                style={{ fontSize: '16px' }}
                color={
                  record.criteriaSet?.criterias.reduce((x: any, y: any) => x + y.answer, 0) /
                    record.criteriaSet?.criterias.length >
                  85
                    ? '#52c41a'
                    : record.criteriaSet?.criterias.reduce((x: any, y: any) => x + y.answer, 0) /
                        record.criteriaSet?.criterias.length >
                      60
                    ? '#faad14'
                    : ''
                }
              />
              // <Progress
              //   type="circle"
              //   percent={
              //     record.criteriaSet.criterias.reduce((x: any, y: any) => x + y.answer, 0) /
              //     record.criteriaSet.criterias.length
              //   }
              //   width={65}
              //   format={() =>
              //     `${
              //       record.criteriaSet.criterias.reduce((x: any, y: any) => x + y.answer, 0) /
              //       record.criteriaSet.criterias.length
              //     }%`
              //   }
              // />
            )}
          </Space>
        ),
      },
    ];
  }, [submitData]);

  useEffect(() => {
    const token = connection.hasSession();
    if (token) {
      setLoading(true);
      connection
        .get<{ performanceCycle: PerformanceCycle[]; closestCycle: PerformanceCycle }>('cycles')
        .then((value) => {
          setCycles(value.performanceCycle);
          if (cycleRef.current === undefined) {
            setCycle(value.closestCycle.id, 'replace');
          }
        })
        .catch(() => setLoading(false));
    }
  }, [connection, setCycle]);

  const handleObjection = useCallback(
    (action: 'Accept' | 'Reject', reason?: string) => {
      setLoading(true);
      const payload: { action: 'Accept' | 'Reject'; recallReason?: string } = { action };

      if (action === 'Accept' && reason) {
        payload.recallReason = reason;
      }

      connection
        .patch(`goals/internal-customers/${record?.id}/handle-objection`, payload)
        .then(() => {
          setFetching(!fetching);
          setRecallReason('');
          setRecallModalMode('recall');
          setRecallModalVisible(false);
          setModalVisible(false);
        })
        .catch((e) => {
          setLoading(false);
          message.error(e?.message || 'Failed to handle objection');
          console.error(e);
        });
    },
    [connection, fetching, record?.id],
  );

  const approveReview = useCallback(
    (x: string, reason?: string) => {
      setLoading(true);
      const payload: any = {
        answers: submitData?.criteriaSet?.map((e: any) => ({
          id: e.id,
          answer: ((e.answer ?? 0) / e.type) * 100,
          comment: e.comment,
        })),
      };
      if (x === 'reject') {
        payload.rejectionReason = reason;
      } else {
        payload.overallComment = submitData?.overallComment;
      }
      connection
        .patch(`goals/internal-customers/${record?.id}/${x}`, payload)
        .then(() => {
          setSubmitData({
            criteriaSet: [],
            overallComment: '',
            submitTime: '',
            cycle: cycle,
            submittingUser: 0,
          });
          setFetching(!fetching);
          window.onbeforeunload = null;
          setModalVisible(false);
        })
        .catch((e) => {
          setLoading(false);
          console.log(e);
        });
    },
    [connection, cycle, fetching, record?.id, submitData?.criteriaSet, submitData?.overallComment],
  );

  const showApproveModal = useCallback(() => {
    confirm({
      title: 'Approving the review',
      icon: <ExclamationCircleOutlined />,
      content: `Are you sure to approve?`,
      okText: `Approve`,
      centered: true,
      okType: 'primary',
      cancelText: 'Cancel',
      onOk() {
        approveReview('approve');
      },
      onCancel() {
        console.log('Cancel');
      },
    });
  }, [approveReview]);

  const showRejectModal = useCallback(() => {
    // Open custom modal to capture rejection reason
    setRejectReason('');
    setRejectModalVisible(true);
  }, []);

  const recallReview = useCallback(
    (reason: string) => {
      setLoading(true);
      connection
        .patch(`goals/internal-customers/${record?.id}/recall`, { recallReason: reason })
        .then(() => {
          setSubmitData({
            criteriaSet: [],
            overallComment: '',
            submitTime: '',
            cycle: cycle,
            submittingUser: 0,
          });
          setFetching(!fetching);
          window.onbeforeunload = null;
          setRecallReason('');
          setRecallModalMode('recall');
          setRecallModalVisible(false);
          setModalVisible(false);
        })
        .catch((e) => {
          setLoading(false);
          console.log(e);
        });
    },
    [connection, cycle, fetching, record?.id],
  );

  const showRecallModal = useCallback((mode: 'recall' | 'accept-objection' = 'recall') => {
    setRecallReason('');
    setRecallModalMode(mode);
    if (mode === 'accept-objection') {
      setModalVisible(false);
    }
    setRecallModalVisible(true);
  }, []);

  return (
    <WebUxPage>
      <Modal
        title={recallModalMode === 'accept-objection' ? 'Accept Objection' : 'Recall ICR Feedback'}
        visible={recallModalVisible}
        okText={recallModalMode === 'accept-objection' ? 'Accept and Recall' : 'Recall'}
        okType="primary"
        confirmLoading={loading}
        closable={!loading}
        maskClosable={!loading}
        keyboard={!loading}
        onOk={() => {
          const plain = recallReason.replace(regex, '').trim();
          if (!plain) return;
          if (recallModalMode === 'accept-objection') {
            handleObjection('Accept', recallReason);
            return;
          }
          recallReview(recallReason);
        }}
        okButtonProps={{
          disabled: loading || !recallReason || !recallReason.replace(regex, '').trim().length,
        }}
        cancelButtonProps={{
          disabled: loading,
        }}
        onCancel={() => {
          if (loading) return;
          setRecallReason('');
          setRecallModalMode('recall');
          setRecallModalVisible(false);
        }}
        centered>
        <Typography.Paragraph>
          {recallModalMode === 'accept-objection'
            ? 'Please provide the recall reason for accepting this objection. This will be shown to the submitter.'
            : 'Please provide a reason for recalling this internal customer feedback. This will be shown to the submitter.'}
        </Typography.Paragraph>
        <RichText
          placeholder={
            recallModalMode === 'accept-objection'
              ? 'Add recall reason for objection acceptance'
              : 'Add recall reason'
          }
          value={recallReason}
          maxLength={10000}
          onChange={(val) => setRecallReason(val)}
        />
      </Modal>
      <Modal
        title="Rejecting the review"
        visible={rejectModalVisible}
        okText="Reject"
        okType="primary"
        onOk={() => {
          // require a reason
          const plain = rejectReason.replace(regex, '').trim();
          if (!plain) return;
          setRejectModalVisible(false);
          approveReview('reject', rejectReason);
        }}
        okButtonProps={{
          disabled: !rejectReason || !rejectReason.replace(regex, '').trim().length,
        }}
        onCancel={() => setRejectModalVisible(false)}
        centered>
        <Typography.Paragraph>
          Please provide a reason for rejecting this internal customer feedback. This will be shown
          to the user.
        </Typography.Paragraph>
        <RichText
          placeholder={'Add rejection reason'}
          value={rejectReason}
          maxLength={10000}
          onChange={(val) => setRejectReason(val)}
        />
      </Modal>
      <PageHeader
        ghost={false}
        style={{ background: '#FFF' }}
        title="Internal Customer Review Answers"
        subTitle="Manage Internal Customer review answers"
        // breadcrumb={{
        //   routes: [
        //     {
        //       path: 'goals',
        //       breadcrumbName: `Internal Customer Review Answers`,
        //     },
        //   ],
        // }}
      />
      <WebUxPageContent>
        <WebUxPageItem>
          <FlexBox>
            <CycleToggle
              value={parseInt(`${cycle}`)}
              onChange={(value) => setCycle(value, 'replace')}
              data={cycles}
            />
            <FlexBox style={{ marginLeft: '3%', alignItems: 'center' }}>
              <Typography style={{ marginRight: '2%' }}>Selected Team :- </Typography>
              <Select
                showSearch
                style={{ width: '250px' }}
                onChange={(value) => {
                  setFilterTeam(value);
                  setPagination({ size: pagination.size, page: 1 });
                  search(filter, { size: pagination.size, page: 1 }, value);
                }}
                value={filterTeam}
                filterOption={(input, option) =>
                  option?.props?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }>
                <Select.Option key="all-teams" value="">
                  {'All Teams'}
                </Select.Option>
                {teams
                  ?.map((e: any) => ({ teamName: e.value, teamId: e.id }))
                  .filter(
                    (v: { teamId: any }, i: any, a: any[]) =>
                      a.findIndex((v2) => v2.teamId === v.teamId) === i,
                  )
                  .map((item: any) => (
                    <Select.Option key={`team-${item.teamId}`} value={item.teamId}>
                      {item.teamName}
                    </Select.Option>
                  ))}
              </Select>
            </FlexBox>
          </FlexBox>
          <FlexBox
            style={{
              marginBottom: '10px',
              alignItems: 'end',
              justifyContent: 'space-between',
            }}>
            <div></div>
            <Space direction="vertical">
              <Segmented
                options={[
                  {
                    label: 'All',
                    value: 'all',
                  },
                  {
                    label: 'Not Submitted',
                    value: 'not_submitted',
                  },
                  // {
                  //   label: 'Pending Approval',
                  //   value: 'pending_approved',
                  // },
                  {
                    label: 'Submitted',
                    value: 'submitted',
                  },
                  {
                    label: 'Recalled',
                    value: 'recalled',
                  },
                  {
                    label: 'Rejected',
                    value: 'rejected',
                  },
                  {
                    label: 'Approved',
                    value: 'approved',
                  },
                  {
                    label: 'Objection Pending',
                    value: 'objection_pending',
                  },
                  {
                    label: 'Objection Accepted',
                    value: 'objection_accepted',
                  },
                  {
                    label: 'Objection Rejected',
                    value: 'objection_rejected',
                  },
                ]}
                onChange={(e) => {
                  setFilterType(e as string);
                  // setSelectedRowKeys([]);
                }}
              />
            </Space>
          </FlexBox>
          <DataTable
            columns={columns}
            total={total}
            loading={loading}
            expandable={{ indentSize: 0 }}
            dataSource={tableData}
            onPaginationChange={(p) => {
              setPagination(p);
              if (pagination.page !== p.page && pagination.size === p.size) {
                search(filter, p, filterTeam);
              } else if (pagination.size !== p.size) {
                setPagination({ ...p, page: 1 });
                search(filter, { ...p, page: 1 }, filterTeam);
              }
            }}
            dtPagination={pagination}
          />
        </WebUxPageItem>
      </WebUxPageContent>
      <Modal
        open={modalVisible}
        destroyOnClose
        onCancel={() => {
          setSubmitData({
            criteriaSet: [],
            overallComment: '',
            submitTime: '',
            cycle: cycle,
            submittingUser: 0,
          });
          setRecord(undefined);
          window.onbeforeunload = null;
          setModalVisible(false);
        }}
        footer={
          record?.objectionStatus === 'Pending'
            ? [
                <Button key="cancel" onClick={() => setModalVisible(false)}>
                  Cancel
                </Button>,
                <Button
                  key="reject-objection"
                  danger
                  loading={loading}
                  onClick={() => handleObjection('Reject')}>
                  Reject Objection
                </Button>,
                <Button
                  key="accept-objection"
                  type="primary"
                  loading={loading}
                  onClick={() => showRecallModal('accept-objection')}>
                  Accept Objection
                </Button>,
              ]
            : record?.status == 'approved'
            ? [
                <Button
                  key="cancel"
                  type="default"
                  disabled={loading}
                  onClick={() => {
                    setSubmitData({
                      criteriaSet: [],
                      overallComment: '',
                      submitTime: '',
                      cycle: cycle,
                      submittingUser: 0,
                    });
                    setRecord(undefined);
                    window.onbeforeunload = null;
                    setModalVisible(false);
                  }}>
                  Cancel
                </Button>,
                // <Button
                //   key="resubmit"
                //   type="primary"
                //   disabled={
                //     submitData?.overallComment === '' ||
                //     submitData?.criteriaSet?.length === 0 ||
                //     submitData?.criteriaSet?.findIndex(
                //       (e: any) => e.answer === 0 || e.answer === null || e.answer === undefined,
                //       // e.comment === '' ||
                //       // e.comment === null ||
                //       // e.comment === undefined,
                //     ) !== -1
                //   }
                //   loading={loading}
                //   onClick={() => showApproveModal()}>
                //   Re-Submit And Approve
                // </Button>,
                <Button
                  key="recall"
                  danger
                  loading={loading}
                  disabled={record?.isRecallDisabled}
                  onClick={() => showRecallModal()}>
                  Recall
                </Button>,
              ]
            : [
                <Button
                  key="cancel"
                  type="default"
                  disabled={loading}
                  onClick={() => {
                    setSubmitData({
                      criteriaSet: [],
                      overallComment: '',
                      submitTime: '',
                      cycle: cycle,
                      submittingUser: 0,
                    });
                    setRecord(undefined);
                    window.onbeforeunload = null;
                    setModalVisible(false);
                  }}>
                  Cancel
                </Button>,
                <Button key="reject" danger loading={loading} onClick={() => showRejectModal()}>
                  Reject
                </Button>,
                <Button
                  key="approve"
                  type="primary"
                  disabled={
                    submitData?.overallComment === '' ||
                    submitData?.criteriaSet?.length === 0 ||
                    submitData?.criteriaSet?.findIndex(
                      (e: any) => e.answer === 0 || e.answer === null || e.answer === undefined,
                      // e.comment === '' ||
                      // e.comment === null ||
                      // e.comment === undefined,
                    ) !== -1
                  }
                  loading={loading}
                  onClick={() => showApproveModal()}>
                  Approve
                </Button>,
              ]
        }>
        {record && (
          <>
            <Row style={{ marginTop: '3%' }}>
              <UserAvatar size={80} />
              <Col style={{ marginLeft: '3%', marginTop: '1%' }}>
                <Row style={{ alignItems: 'center' }}>
                  <Space>
                    <Title level={3} style={{ margin: 0 }}>
                      {record?.teamName}
                    </Title>
                    <div>
                      <Tag color={record?.teamType?.color}>{record?.teamType?.name}</Tag>
                    </div>
                  </Space>
                </Row>
                <Text type="secondary">{record?.teamDescription}</Text>
              </Col>
            </Row>
            {record.objectionReason && (
              <div
                style={{
                  marginTop: '2%',
                  padding: '10px',
                  background: '#fff1f0',
                  border: '1px solid #ffa39e',
                  borderRadius: '4px',
                }}>
                <Space>
                  <ExclamationCircleOutlined style={{ color: '#f5222d' }} />
                  <Text strong>Objection Received:</Text>
                </Space>
                <div style={{ marginTop: 5 }}>
                  {record.objectionReason && !!record.objectionReason?.replace(regex, '').length
                    ? ReactHtmlParser(record.objectionReason)
                    : ''}
                </div>
              </div>
            )}
            <Divider style={{ marginTop: '2%' }} />
            <div style={{ marginLeft: '1%' }}>
              <ReviewPreview mode={'view'} criterias={submitData?.criteriaSet} />
            </div>
            <Space style={{ marginTop: '2%' }}>
              <StarFilled style={{ color: 'red', fontSize: 7, paddingRight: 5 }} />
              <AlignLeftOutlined style={{ color: 'rgba(0, 0, 0, 0.45)' }} />
              <Text type="secondary">Overall Comment</Text>
            </Space>
            <Divider orientation="left" style={{ marginTop: 0, marginBottom: 0 }} />
            <div style={{ marginTop: '2%' }}>
              <Typography>
                {submitData?.overallComment &&
                !!submitData?.overallComment?.replace(regex, '').length
                  ? ReactHtmlParser(submitData?.overallComment)
                  : ''}
              </Typography>
            </div>
            <Row style={{ alignItems: 'center', marginTop: 20 }}>
              <Space>
                <Title level={5}>Activity</Title>
                <RetweetOutlined style={{ fontSize: 20 }} />
              </Space>
            </Row>
            <Link onClick={() => setShowContent(!showContent)}>
              <Space className="div_hover">
                <Text>Show Activities</Text>
                {!showContent ? (
                  <CaretDownOutlined style={{ marginTop: 7, color: 'rgba(0,0,0,0.7)' }} />
                ) : (
                  <CaretUpOutlined style={{ marginTop: 7, color: 'rgba(0,0,0,0.7)' }} />
                )}
              </Space>
            </Link>
            {record?.internalCustomerReviewHistory?.length !== 0 &&
              showContent &&
              record?.internalCustomerReviewHistory?.map((item: any, key: any) => {
                const objectionStatusMeta = getObjectionStatusMeta(item.objectionStatus);

                return (
                  <div key={key} className="borderBox">
                    <Row>
                      <Space>
                        <UserAvatar src={item?.image} />
                        <Text strong>{item?.name}</Text>
                        {/*<Text type="secondary">{item.date + ' ' + item.time}</Text>*/}
                      </Space>
                    </Row>
                    <Row>
                      <Text strong>
                        Status:
                        {(record.status == '' || record.status == 'not submitted') && (
                          <Tag color={'blue'}>Not submitted</Tag>
                        )}
                        {item.status == 'submitted' && <Tag color={'yellow'}>Submitted</Tag>}
                        {item.status == 'approved' && <Tag color={'green'}>Approved</Tag>}
                        {item.status == 'rejected' && <Tag color={'red'}>Rejected</Tag>}
                        {item.status == 'recalled' && <Tag color={'orange'}>Recalled</Tag>}
                      </Text>
                      <Text type="secondary">{item.date}</Text>
                    </Row>
                    {item?.status === 'recalled' && (
                      <div style={{ marginBottom: 10 }}>
                        <Row style={{ marginLeft: 20 }}>
                          <Text strong>Recall Reason</Text>
                        </Row>
                        <Row style={{ marginLeft: 20 }}>
                          <Text type="secondary">
                            {item.recallReason && !!item.recallReason?.replace(regex, '').length
                              ? ReactHtmlParser(item.recallReason)
                              : ''}
                          </Text>
                        </Row>
                      </div>
                    )}
                    {item?.objectionReason && (
                      <div style={{ marginBottom: 10 }}>
                        <Row style={{ marginLeft: 20 }}>
                          <Space>
                            <Text strong>Objection</Text>
                            {objectionStatusMeta && (
                              <Tag color={objectionStatusMeta.color}>
                                {objectionStatusMeta.label}
                              </Tag>
                            )}
                          </Space>
                        </Row>
                        <Row style={{ marginLeft: 20 }}>
                          <Text type="secondary">
                            {item.objectionReason &&
                            !!item.objectionReason?.replace(regex, '').length
                              ? ReactHtmlParser(item.objectionReason)
                              : ''}
                          </Text>
                        </Row>
                      </div>
                    )}
                    {item?.status === 'submitted' &&
                      item.history?.map((e: any, index1: any) => {
                        return (
                          <div key={index1}>
                            <Row style={{ marginTop: 20 }}>
                              <Text type="secondary">{`Submitted ${e.criteriaName}`}</Text>
                            </Row>
                            {e.newAnswer !== 0 && (
                              <div>
                                <Row style={{ marginLeft: 20 }}>
                                  <Text strong>Answer</Text>
                                </Row>
                                <Row style={{ marginLeft: 20 }}>
                                  <Rate
                                    value={(e.newAnswer / 100) * e.criteriaType ?? 0}
                                    character={<StarFilled style={{ fontSize: 15 }} />}
                                    count={e.type}
                                    disabled={true}
                                  />
                                </Row>
                              </div>
                            )}
                            {e.newAnswer !== 0 && (
                              <div>
                                <Row style={{ marginLeft: 20 }}>
                                  <Text strong>Comment</Text>
                                </Row>
                                <Row style={{ marginLeft: 20 }}>
                                  <Text type="secondary">
                                    {e.newComment && !!e.newComment?.replace(regex, '').length
                                      ? ReactHtmlParser(e.newComment)
                                      : ''}
                                  </Text>
                                </Row>
                              </div>
                            )}
                            {e.newAnswer === 0 && e.oldAnswer === 0 && (
                              <div>
                                <Row style={{ marginLeft: 20 }}>
                                  <Text strong>Comment</Text>
                                </Row>
                                <Row style={{ marginLeft: 20 }}>
                                  <Text type="secondary">
                                    {e.newComment && !!e.newComment?.replace(regex, '').length
                                      ? ReactHtmlParser(e.newComment)
                                      : ''}
                                  </Text>
                                </Row>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    {item?.status !== 'submitted' &&
                      item.history?.map((e: any, index1: any) => {
                        return (
                          <div key={index1}>
                            <Row style={{ marginTop: 10 }}>
                              <Text type="secondary">{`Updated ${e.criteriaName}`}</Text>
                            </Row>
                            {e.newAnswer !== 0 && (
                              <div>
                                <Row style={{ marginLeft: 20 }}>
                                  <Text strong>Answer</Text>
                                </Row>
                                <Row
                                  style={{ marginLeft: 20, display: 'flex', alignItems: 'center' }}>
                                  <Rate
                                    value={(e.oldAnswer / 100) * e.criteriaType ?? 0}
                                    character={<StarFilled style={{ fontSize: 15 }} />}
                                    count={e.type}
                                    disabled={true}
                                  />
                                  <ArrowRightOutlined
                                    style={{ paddingTop: 6, paddingLeft: 15, paddingRight: 15 }}
                                  />
                                  <Rate
                                    value={(e.newAnswer / 100) * e.criteriaType ?? 0}
                                    character={<StarFilled style={{ fontSize: 15 }} />}
                                    count={e.type}
                                    disabled={true}
                                  />
                                </Row>
                              </div>
                            )}
                            {e.newAnswer !== 0 && (
                              <div>
                                <Row style={{ marginLeft: 20 }}>
                                  <Text strong>Comment</Text>
                                </Row>

                                <Row
                                  style={{ marginLeft: 20, display: 'flex', alignItems: 'center' }}>
                                  <Text type="secondary">
                                    {e.oldComment && !!e.oldComment?.replace(regex, '').length
                                      ? ReactHtmlParser(e.oldComment)
                                      : ''}
                                  </Text>
                                  <ArrowRightOutlined
                                    style={{ paddingLeft: 15, paddingRight: 15 }}
                                  />
                                  <Text type="secondary">
                                    {e.newComment && !!e.newComment?.replace(regex, '').length
                                      ? ReactHtmlParser(e.newComment)
                                      : ''}
                                  </Text>
                                </Row>
                              </div>
                            )}
                            {e.newAnswer === 0 && e.oldAnswer === 0 && (
                              <div>
                                <Row style={{ marginLeft: 20 }}>
                                  <Text strong>Comment</Text>
                                </Row>
                                <Row style={{ marginLeft: 20 }}>
                                  <Text type="secondary">
                                    {e.oldComment && !!e.oldComment?.replace(regex, '').length
                                      ? ReactHtmlParser(e.oldComment)
                                      : ''}
                                  </Text>
                                  <ArrowRightOutlined
                                    style={{ paddingLeft: 15, paddingRight: 15 }}
                                  />
                                  <Text type="secondary">
                                    {'   '}
                                    {e.newComment && !!e.newComment?.replace(regex, '').length
                                      ? ReactHtmlParser(e.newComment)
                                      : ''}
                                  </Text>
                                </Row>
                              </div>
                            )}
                          </div>
                        );
                      })}
                  </div>
                );
              })}
          </>
        )}
      </Modal>
    </WebUxPage>
  );
};

export default InternalCustomerAnswers;
